(function () {
  const MOBILE_MAX = 1040;
  const MOBILE_GAP = 4;
  const BASE_ACTIVATOR_OFFSET = 20;
  const INBOX_Z_INDEX = 99990;
  const MOBILE_SELECTORS = ['[data-mobile-bar]', '.cart-mobile-bar', '[data-cart-mobile-bar]'];
  const CHAT_HOST_SELECTORS = ['shopify-chat', 'shopify-agent', 'shopify-agent-runtime', '#shopify-chat'];
  const LEGACY_IFRAME_SELECTORS = ['#dummy-chat-button-iframe'];
  const observedBars = new WeakSet();
  const observedInboxNodes = new WeakSet();
  let barResizeObserver = null;
  let domObserver = null;
  let syncFrame = null;

  function getViewportBottom() {
    if (!window.visualViewport) return window.innerHeight;
    return window.visualViewport.offsetTop + window.visualViewport.height;
  }

  function isVisible(block) {
    if (!block) return false;
    const styles = window.getComputedStyle(block);
    if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) {
      return false;
    }
    const rect = block.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0;
  }

  function isMobileViewport() {
    return window.innerWidth <= MOBILE_MAX;
  }

  function getOffsetBlocks() {
    if (!isMobileViewport()) return [];

    const seen = new Set();
    const blocks = [];

    MOBILE_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (block) {
        if (seen.has(block) || !isVisible(block)) return;
        seen.add(block);
        blocks.push(block);
      });
    });

    return blocks;
  }

  function getBlockOffsetFromBottom(block) {
    const rect = block.getBoundingClientRect();
    const viewportBottom = getViewportBottom();
    if (rect.bottom <= 0 || rect.top >= viewportBottom) return 0;
    return Math.max(0, Math.ceil(viewportBottom - rect.top));
  }

  function computeBarOffset() {
    if (!isMobileViewport()) return 0;

    const blocks = getOffsetBlocks();
    if (!blocks.length) return 0;

    let maxOffset = 0;

    blocks.forEach(function (block) {
      maxOffset = Math.max(maxOffset, getBlockOffsetFromBottom(block));
    });

    return Math.max(0, maxOffset + MOBILE_GAP);
  }

  function hasModernChatHost() {
    return getChatHosts().some(function (host) {
      return host.matches('shopify-chat, shopify-agent, shopify-agent-runtime');
    });
  }

  function getChatHosts() {
    const hosts = [];
    const seen = new Set();

    CHAT_HOST_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (node) {
        if (seen.has(node)) return;
        seen.add(node);
        hosts.push(node);
      });
    });

    return hosts;
  }

  function isChatOpen() {
    const hosts = getChatHosts();

    if (hosts.some(function (host) {
      return host.matches('shopify-chat, shopify-agent, shopify-agent-runtime')
        && (host.hasAttribute('open') || host.getAttribute('data-open') === 'true');
    })) {
      return true;
    }

    const legacyRoot = document.getElementById('shopify-chat');
    if (!legacyRoot) return false;

    return Array.from(legacyRoot.querySelectorAll('iframe')).some(function (iframe) {
      if (iframe.id === 'dummy-chat-button-iframe') return false;
      const style = (iframe.getAttribute('style') || '').toLowerCase();
      if (style.includes('min-height') || style.includes('left: 0') || style.includes('left:0')) {
        return true;
      }
      return iframe.getBoundingClientRect().height > 120;
    });
  }

  function setBottom(element, value) {
    if (!element) return;
    if (value == null) {
      element.style.removeProperty('bottom');
      return;
    }
    element.style.setProperty('bottom', value, 'important');
  }

  function applyHostOffset(host, activatorOffset, barOffset, chatOpen) {
    if (!host) return;

    host.style.setProperty('z-index', String(INBOX_Z_INDEX), 'important');

    if (!isMobileViewport()) {
      host.style.setProperty('--shopify-agent-activator-offset', `${BASE_ACTIVATOR_OFFSET}px`);
      host.style.removeProperty('--shopify-chat-y-offset');
      host.style.removeProperty('--shopify-inbox-host-bottom-offset');
      setBottom(host, null);
      return;
    }

    host.style.setProperty('--shopify-agent-activator-offset', `${activatorOffset}px`);
    host.style.setProperty('--shopify-chat-y-offset', `${activatorOffset}px`);

    if (barOffset > 0) {
      host.style.setProperty('--shopify-inbox-host-bottom-offset', `${barOffset}px`);
    } else {
      host.style.removeProperty('--shopify-inbox-host-bottom-offset');
    }

    const closedBottom = !chatOpen && barOffset > 0 ? `${barOffset}px` : null;
    setBottom(host, closedBottom);
  }

  function applyLegacyIframeOffset(barOffset, chatOpen) {
    if (hasModernChatHost()) {
      return;
    }

    if (!isMobileViewport()) {
      LEGACY_IFRAME_SELECTORS.forEach(function (selector) {
        document.querySelectorAll(selector).forEach(function (iframe) {
          iframe.style.setProperty('z-index', String(INBOX_Z_INDEX), 'important');
          iframe.style.removeProperty('display');
          setBottom(iframe, null);
        });
      });

      const legacyRoot = document.getElementById('shopify-chat');
      if (!legacyRoot) return;

      legacyRoot.style.setProperty('z-index', String(INBOX_Z_INDEX), 'important');
      setBottom(legacyRoot, null);
      legacyRoot.querySelectorAll('iframe').forEach(function (iframe) {
        setBottom(iframe, null);
      });
      return;
    }

    const closedBottom = barOffset > 0
      ? `calc(${barOffset}px + env(safe-area-inset-bottom, 0px))`
      : null;
    const buttonIframe = document.getElementById('dummy-chat-button-iframe');

    LEGACY_IFRAME_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (iframe) {
        iframe.style.setProperty('z-index', String(INBOX_Z_INDEX), 'important');
        if (chatOpen) {
          iframe.style.setProperty('display', 'none', 'important');
          return;
        }
        iframe.style.removeProperty('display');
        setBottom(iframe, closedBottom);
      });
    });

    const legacyRoot = document.getElementById('shopify-chat');
    if (!legacyRoot) return;

    legacyRoot.style.setProperty('z-index', String(INBOX_Z_INDEX), 'important');
    if (chatOpen) {
      legacyRoot.querySelectorAll('iframe').forEach(function (iframe) {
        if (iframe.id === 'dummy-chat-button-iframe') return;
        setBottom(
          iframe,
          barOffset > 0 && window.innerWidth <= MOBILE_MAX ? closedBottom : null
        );
      });
      return;
    }

    setBottom(legacyRoot, closedBottom);
    legacyRoot.querySelectorAll('iframe').forEach(function (iframe) {
      if (iframe.id === 'dummy-chat-button-iframe') {
        setBottom(iframe, closedBottom);
      } else {
        setBottom(iframe, null);
      }
    });

    if (buttonIframe && !chatOpen) {
      buttonIframe.style.removeProperty('display');
    }
  }

  function applyInboxOffset(barOffset) {
    const chatOpen = isChatOpen();
    const mobileViewport = isMobileViewport();
    const effectiveOffset = mobileViewport ? barOffset : 0;
    const activatorOffset = effectiveOffset > 0 ? effectiveOffset : BASE_ACTIVATOR_OFFSET;

    document.documentElement.style.setProperty('--shopify-inbox-bottom-offset', `${effectiveOffset}px`);
    document.documentElement.style.setProperty('--shopify-agent-activator-offset', `${activatorOffset}px`);
    document.documentElement.classList.toggle('shopify-inbox-offset-active', mobileViewport && effectiveOffset > 0);
    document.documentElement.classList.toggle('shopify-inbox-chat-open', chatOpen);

    getChatHosts().forEach(function (host) {
      applyHostOffset(host, activatorOffset, effectiveOffset, chatOpen);
    });

    applyLegacyIframeOffset(effectiveOffset, chatOpen);
  }

  function getInboxTargets() {
    const targets = getChatHosts();
    LEGACY_IFRAME_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (node) {
        targets.push(node);
      });
    });
    document.querySelectorAll('#shopify-chat iframe').forEach(function (iframe) {
      targets.push(iframe);
    });
    return targets;
  }

  function observeBars() {
    getOffsetBlocks().forEach(function (block) {
      if (observedBars.has(block) || !('ResizeObserver' in window)) return;
      if (!barResizeObserver) {
        barResizeObserver = new ResizeObserver(queueSync);
      }
      barResizeObserver.observe(block);
      observedBars.add(block);
    });
  }

  function observeInboxNodes() {
    getInboxTargets().forEach(function (node) {
      if (observedInboxNodes.has(node)) return;
      observedInboxNodes.add(node);
      const styleObserver = new MutationObserver(queueSync);
      styleObserver.observe(node, {
        attributes: true,
        attributeFilter: ['style', 'class', 'open', 'data-open', 'view'],
      });
    });
  }

  function syncInboxOffset() {
    observeBars();
    observeInboxNodes();
    applyInboxOffset(computeBarOffset());
  }

  function queueSync() {
    if (syncFrame !== null) return;
    syncFrame = window.requestAnimationFrame(function () {
      syncFrame = null;
      syncInboxOffset();
    });
  }

  function hasInboxWidget() {
    return getChatHosts().length > 0 || document.getElementById('dummy-chat-button-iframe');
  }

  function init() {
    syncInboxOffset();

    window.addEventListener('resize', queueSync, { passive: true });
    window.addEventListener('orientationchange', queueSync, { passive: true });
    window.addEventListener('scroll', queueSync, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', queueSync, { passive: true });
      window.visualViewport.addEventListener('scroll', queueSync, { passive: true });
    }

    domObserver = new MutationObserver(queueSync);
    domObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'open', 'data-open', 'view'],
    });

    document.addEventListener('shopify:section:load', queueSync);
    document.addEventListener('cart:updated', queueSync);
    document.addEventListener('cart:change', queueSync);

    window.setInterval(function () {
      if (!hasInboxWidget()) return;
      syncInboxOffset();
    }, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
